import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { mediaUrl } from '../api/client';
import { useDeleteMedia, useImportMedia, useMedia, useUploadMedia } from '../api/queries';
import ui from '../components/ui.module.css';
import { EMPTY_RIGHTS, RIGHTS_LABELS, RightsFields, declared } from '../components/RightsFields';
import { errorMessage, fileSize } from '../copy';
import styles from './MediaLibrary.module.css';

/**
 * What the deployment is holding. Two ways in — a file from disk, or a URL the
 * **API** fetches and copies — and one rule on the way out: an image a poll or
 * a candidate points at cannot be deleted (409 `media_in_use`), and the images
 * bundled with the API never can (02-requirements.md §Media Library).
 *
 * The in-use and bundled marks come from the server, not from anything counted
 * here: the panel is a face, and the API is where "in use" is known.
 */
export function MediaLibrary(): ReactNode {
  const { data: images, isLoading, error } = useMedia();
  const upload = useUploadMedia();
  const importUrl = useImportMedia();
  const remove = useDeleteMedia();
  const fileRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [rights, setRights] = useState(EMPTY_RIGHTS);
  const declaration = declared(rights);

  const busy = upload.isPending || importUrl.isPending;

  return (
    <section className={ui.panel}>
      <div className={styles.header}>
        <h1 className={ui.h1}>Imagens</h1>
        <div className={styles.sources}>
          <RightsFields value={rights} onChange={setRights} idPrefix="library" />
          <button
            type="button"
            className={ui.ghost}
            disabled={busy || !declaration}
            onClick={() => fileRef.current?.click()}
          >
            {upload.isPending ? 'Enviando…' : 'Enviar do computador'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file || !declaration) return;
              setFailure(null);
              upload.mutate(
                { file, rights: declaration },
                { onError: (cause) => setFailure(errorMessage(cause)) },
              );
            }}
          />
          <input
            className={`${ui.input} ${styles.url}`}
            placeholder="https://… (o servidor guarda uma cópia)"
            aria-label="Endereço da imagem"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <button
            type="button"
            className={ui.ghost}
            disabled={busy || !url.trim() || !declaration}
            onClick={() => {
              if (!declaration) return;
              setFailure(null);
              importUrl.mutate({ url: url.trim(), rights: declaration }, {
                onSuccess: () => setUrl(''),
                onError: (cause) => setFailure(errorMessage(cause)),
              });
            }}
          >
            {importUrl.isPending ? 'Baixando…' : 'Importar'}
          </button>
        </div>
      </div>

      {failure ? <p className={ui.error}>{failure}</p> : null}
      {error ? <p className={ui.error}>{errorMessage(error)}</p> : null}
      {isLoading ? <p className={ui.muted}>Carregando…</p> : null}
      {images && images.length === 0 ? <p className={ui.muted}>Nenhuma imagem guardada ainda.</p> : null}

      {images && images.length > 0 ? (
        <div className={styles.grid}>
          {images.map((image) => (
            <figure className={styles.card} key={image.name}>
              <img className={styles.image} src={mediaUrl(image.path)} alt={image.name} />
              <figcaption className={styles.name} title={image.name}>
                {image.name}
              </figcaption>
              <div className={styles.meta}>
                <span className={ui.muted}>{fileSize(image.bytes)}</span>
                {image.inUse ? <span className={`${ui.badge} ${ui.badgeActive}`}>EM USO</span> : null}
                {image.bundled ? <span className={`${ui.badge} ${ui.badgeClosed}`}>DO SERVIDOR</span> : null}
                {/* Guardada antes do registro de direitos: revisar ou trocar. */}
                {!image.bundled && !image.rights ? (
                  <span className={`${ui.badge} ${ui.badgePaused}`}>SEM DIREITOS</span>
                ) : null}
              </div>
              {image.rights ? (
                <span className={ui.muted} title={image.rights.source ?? undefined}>
                  {RIGHTS_LABELS[image.rights.basis]}
                  {image.rights.credit ? ` — ${image.rights.credit}` : ''}
                </span>
              ) : null}
              <button
                type="button"
                className={`${ui.ghost} ${ui.small}`}
                disabled={image.inUse || image.bundled || remove.isPending}
                onClick={() => {
                  setFailure(null);
                  remove.mutate(image.name, { onError: (cause) => setFailure(errorMessage(cause)) });
                }}
              >
                Apagar
              </button>
            </figure>
          ))}
        </div>
      ) : null}
    </section>
  );
}
